import { Accordion, Box, Center, Spinner } from '@chakra-ui/react'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { allowExtensionOperations } from '../../../utils/mediaLibrary'
import {
  EmbeddedMetadata,
  FileInfo,
  OtherOperations,
  Tags,
} from '../../components/MediaLibrary'
import type { FileDetailsResponse } from '../../interfaces/FileDetailsResponse'
import PermissionGuard from '../../components/PermissionGuard'
import { checkPermission, PermissionsEnum } from '../../../utils'
import { SideBar } from '../Base'
import { UserContext } from '../../providers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'

const DetailPageSideMenuLayout = ({
  fileDetails,
  isTagsModalOpen,
  toggleTagsModal,
  getFileDetails,
  loading,
}: {
  fileDetails: FileDetailsResponse | undefined
  isTagsModalOpen: boolean
  loading: boolean
  toggleTagsModal(isTagsModalOpen: boolean): void
  getFileDetails(): Promise<FileDetailsResponse>
}): React.ReactElement => {
  const { t } = useTranslation()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)

  const showTagsAccordionItem = useMemo<boolean>(() => {
    if (isPublicLink) {
      return false
    }
    return (
      (fileDetails?.tags?.length ?? 0) > 0 ||
      (fileDetails?.AITags?.length ?? 0) > 0 ||
      checkPermission({
        user,
        permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
      })
    )
  }, [fileDetails, user, isPublicLink])

  const showOtherOpsAccordionItem = useMemo(() => {
    if (isPublicLink) {
      return false
    }
    return (
      fileDetails &&
      allowExtensionOperations(fileDetails) &&
      checkPermission({
        user,
        permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
      })
    )
  }, [fileDetails, user, isPublicLink])

  // this works for 1. fileInfo, 2. embeddedData, 3. tags, 4. Other Operations. Update it if more are added
  const lastOpenAccordionItem = useMemo(() => {
    if (showTagsAccordionItem && showOtherOpsAccordionItem) {
      return 3
    }
    if (showTagsAccordionItem || showOtherOpsAccordionItem) {
      return 2
    }
    return 1
  }, [showTagsAccordionItem, showOtherOpsAccordionItem])

  return (
    <SideBar
      heading={t(
        'file_information_and_actions',
        'File Information and Actions',
      )}
    >
      <Box width="100%" height="100%" position="relative" overflowX="hidden">
        {!loading && fileDetails ? (
          <Accordion
            defaultIndex={[0, lastOpenAccordionItem]}
            allowToggle
            allowMultiple
            borderColor="appGrey"
            overflow="auto"
          >
            <FileInfo fileDetails={fileDetails} isPublicLink={isPublicLink} />
            <EmbeddedMetadata data={fileDetails.embeddedMetadata} />
            {( 
              showTagsAccordionItem ? (
                <Tags
                  fileDetails={fileDetails}
                  isTagsModalOpen={isTagsModalOpen}
                  toggleTagsModal={toggleTagsModal}
                  getFileDetails={getFileDetails}
                /> 
              ): <></>
            )}
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {
                (hasPermission: boolean) => (
                  (hasPermission && showOtherOpsAccordionItem) ? <OtherOperations fileDetails={fileDetails} /> : <></>
                )
              }
            </PermissionGuard>
          </Accordion>
        ) : (
          <Center height="100%">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="appPaleGrey"
              color="appBlue"
              size="xl"
            />
          </Center>
        )}
      </Box>
    </SideBar>
  )
}

export default DetailPageSideMenuLayout
